import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Flex, Text, Button, Select, Separator, IconButton, TextField } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useToast } from "../../../context/ToastContext";
import { useProfileContext, useLocationContext } from "context";
import { useQueryClient } from "@tanstack/react-query";
import { useGetAllBrands, useSaveDotdigitalMappings } from "hooks";
import {
  DOTDIGITAL_CUSTOM_FIELD_MAPPINGS,
  buildDefaultDotdigitalFieldMappings,
  normalizeDotdigitalFieldMappings,
} from "./dotdigitalFieldMappings";

function toStringValue(value) {
  if (value === null || typeof value === "undefined") {
    return "";
  }

  return String(value).trim();
}

function normalizeEmailOptInType(value) {
  return value === "verifiedDouble" ? "verifiedDouble" : "single";
}

function normalizeSyncOnlySubscribedContacts(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value !== "all" && !["false", "0", "no", "off"].includes(value.trim().toLowerCase());
  }

  return true;
}

function normalizeDotdigitalLists(lists) {
  return (Array.isArray(lists) ? lists : [])
    .map((list) => ({
      id: toStringValue(list?.id ?? list?.list_id),
      name: toStringValue(list?.name ?? list?.list_name),
    }))
    .filter((list) => list.id || list.name);
}

function getRegionCode(region) {
  const regionValue = toStringValue(region);
  const match = /(?:https?:\/\/)?(r\d+)-app\.dotdigital\.com\/?/i.exec(regionValue);

  if (match?.[1]) {
    return match[1].toUpperCase();
  }

  return regionValue.replace(/^https?:\/\//, "").replace(/\/$/, "") || "Region";
}

function buildDotdigitalConfigId(rawConfig, fallbackIndex = 0) {
  const explicitId = rawConfig?.id ?? rawConfig?.dotdigital_config_id ?? rawConfig?.config_id ?? rawConfig?.persistedId ?? null;

  if (explicitId !== null && typeof explicitId !== "undefined" && String(explicitId).trim()) {
    return String(explicitId).trim();
  }

  const apiUser = toStringValue(rawConfig?.api_user ?? rawConfig?.apiUser);
  const accountRegion = toStringValue(rawConfig?.account_region ?? rawConfig?.accountRegion);

  if (apiUser || accountRegion) {
    return `draft:${apiUser}::${accountRegion}`;
  }

  return `draft:config-${fallbackIndex + 1}`;
}

function normalizeDotdigitalConfigs(rawConfigs = [], fallbackConfig = null, fallbackLists = []) {
  const sourceConfigs = Array.isArray(rawConfigs) && rawConfigs.length > 0
    ? rawConfigs
    : fallbackConfig
      ? [{ ...fallbackConfig, lists: fallbackLists }]
      : [];

  return sourceConfigs
    .map((rawConfig, index) => {
      const id = buildDotdigitalConfigId(rawConfig, index);
      const persistedId = rawConfig?.persistedId ?? rawConfig?.id ?? rawConfig?.dotdigital_config_id ?? rawConfig?.config_id ?? null;
      const apiUser = toStringValue(rawConfig?.api_user ?? rawConfig?.apiUser);
      const accountRegion = toStringValue(rawConfig?.account_region ?? rawConfig?.accountRegion) || "https://r1-app.dotdigital.com/";
      const emailOptInType = normalizeEmailOptInType(rawConfig?.email_opt_in_type ?? rawConfig?.emailOptInType);
      const syncOnlySubscribedContacts = normalizeSyncOnlySubscribedContacts(rawConfig?.sync_only_subscribed_contacts ?? rawConfig?.syncOnlySubscribedContacts);
      const fieldMappings = normalizeDotdigitalFieldMappings(rawConfig?.field_mappings ?? rawConfig?.fieldMappings);
      const lists = normalizeDotdigitalLists(rawConfig?.lists ?? rawConfig?.address_books ?? (sourceConfigs.length === 1 ? fallbackLists : []));

      return {
        id,
        persistedId: persistedId === null || typeof persistedId === "undefined" ? null : String(persistedId),
        apiUser,
        accountRegion,
        emailOptInType,
        syncOnlySubscribedContacts,
        fieldMappings,
        lists,
        label: toStringValue(rawConfig?.label ?? rawConfig?.name)
          || [apiUser || `Account ${index + 1}`, getRegionCode(accountRegion)].filter(Boolean).join(" • "),
      };
    })
    .filter((config) => config.apiUser || config.lists.length > 0);
}

function getDotdigitalConfigLabel(config) {
  if (!config) {
    return "Select a Dotdigital account";
  }

  return config.label || [config.apiUser, getRegionCode(config.accountRegion)].filter(Boolean).join(" • ");
}

function getDotdigitalConfigLists(configs, configId) {
  if (!configId) {
    return [];
  }

  return (configs || []).find((config) => String(config.id) === String(configId))?.lists || [];
}

export function DotdigitalConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const queryClient = useQueryClient();
  const saveDotdigitalMappings = useSaveDotdigitalMappings();

  const [dotdigitalConfigs, setDotdigitalConfigs] = useState([]);
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});
  const [selectedFieldMappingConfigId, setSelectedFieldMappingConfigId] = useState("");
  const [fieldMappingsByConfigId, setFieldMappingsByConfigId] = useState({});
  const [originalFieldMappingsByConfigId, setOriginalFieldMappingsByConfigId] = useState({});
  const [brandSelections, setBrandSelections] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { data: brandsData, isLoading: isLoadingBrands } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();

  useEffect(() => {
    const normalizedConfigs = normalizeDotdigitalConfigs(
      location.state?.configs || [],
      location.state?.credentials || null,
      location.state?.lists || []
    );
    const initialFieldMappings = Object.fromEntries(
      normalizedConfigs.map((config) => [config.id, normalizeDotdigitalFieldMappings(config.fieldMappings)])
    );

    setDotdigitalConfigs(normalizedConfigs);
    setSelectedFieldMappingConfigId(normalizedConfigs[0]?.id || "");
    setFieldMappingsByConfigId(initialFieldMappings);
    setOriginalFieldMappingsByConfigId(initialFieldMappings);
  }, [location.state]);

  useEffect(() => {
    if (!locationsData || dotdigitalConfigs.length === 0 || Object.keys(locationMappings).length > 0) {
      return;
    }

    const initialMappings = {};

    (locationsData || []).forEach((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      initialMappings[key] = {
        dotdigital_config_id: "",
        dotdigital_list_id: "",
        isMapped: false,
      };
    });

    setLocationMappings(initialMappings);
    setOriginalLocationMappings(
      Object.fromEntries(
        Object.entries(initialMappings).map(([key, value]) => [
          key,
          {
            dotdigital_config_id: value.dotdigital_config_id,
            dotdigital_list_id: value.dotdigital_list_id,
          },
        ])
      )
    );
  }, [locationsData, dotdigitalConfigs, locationMappings]);

  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((brand) => brand.is_default);
      const nonDefaultBrands = brandsData.filter((brand) => !brand.is_default);
      setBconnectBrands(nonDefaultBrands || []);
      const defaultLocations = locationsData.filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
      return;
    }

    setBconnectBrands([]);
    setIndependentLocations([]);
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};

    if (!bconnectBrands) {
      return map;
    }

    bconnectBrands.forEach((brand) => {
      const sourceBrand = (brandsData || []).find((brandData) => String(brandData.id) === String(brand.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[brand.id] = sourceBrand.locations;
        return;
      }

      map[brand.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(brand.id));
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  const checkHalfFilled = (locations) => {
    return (locations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const mapping = locationMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "" };
      const hasConfig = Boolean(mapping.dotdigital_config_id);
      const hasList = Boolean(mapping.dotdigital_list_id);

      return (hasConfig && !hasList) || (!hasConfig && hasList);
    });
  };

  const handleLocationConfigChange = (bconnectLocationId, configId) => {
    const key = String(bconnectLocationId);

    setLocationMappings((previousMappings) => {
      const nextMappings = { ...(previousMappings || {}) };
      const existing = nextMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "", isMapped: false };
      const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, configId);
      const dotdigitalListId = availableLists.some((list) => String(list.id) === String(existing.dotdigital_list_id))
        ? existing.dotdigital_list_id
        : "";

      nextMappings[key] = {
        ...existing,
        dotdigital_config_id: configId,
        dotdigital_list_id: dotdigitalListId,
        isMapped: Boolean(configId && dotdigitalListId),
      };
      return nextMappings;
    });
  };

  const handleLocationDotdigitalListChange = (bconnectLocationId, dotdigitalListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((previousMappings) => {
      const nextMappings = { ...(previousMappings || {}) };
      const existing = nextMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "", isMapped: false };
      nextMappings[key] = {
        ...existing,
        dotdigital_list_id: dotdigitalListId,
        isMapped: Boolean(existing.dotdigital_config_id && dotdigitalListId),
      };
      return nextMappings;
    });
  };

  const handleBrandConfigSelectionChange = (brandId, configId) => {
    setBrandSelections((previousSelections) => {
      const brandKey = String(brandId);
      const existing = previousSelections?.[brandKey] || { configId: "", listId: "" };
      const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, configId);
      const listId = availableLists.some((list) => String(list.id) === String(existing.listId))
        ? existing.listId
        : "";

      return {
        ...(previousSelections || {}),
        [brandKey]: {
          configId,
          listId,
        },
      };
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandSelections((previousSelections) => ({
      ...(previousSelections || {}),
      [String(brandId)]: {
        ...(previousSelections?.[String(brandId)] || { configId: "", listId: "" }),
        listId,
      },
    }));
  };

  const handleApplyBrandList = (brandId) => {
    const selectedConfig = brandSelections[String(brandId)]?.configId;
    const selectedList = brandSelections[String(brandId)]?.listId;

    if (!selectedConfig || !selectedList) {
      return;
    }

    const brandLocations = brandLocationsMap[brandId] || [];
    setLocationMappings((previousMappings) => {
      const nextMappings = { ...(previousMappings || {}) };

      brandLocations.forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = nextMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "", isMapped: false };
        nextMappings[key] = {
          ...existing,
          dotdigital_config_id: selectedConfig,
          dotdigital_list_id: selectedList,
          isMapped: Boolean(selectedConfig && selectedList),
        };
      });

      return nextMappings;
    });
  };

  const hasBrandSectionChanges = useMemo(() => {
    return (allBrandOwnedLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const currentConfig = String(locationMappings[key]?.dotdigital_config_id || "");
      const originalConfig = String(originalLocationMappings[key]?.dotdigital_config_id || "");
      const currentList = String(locationMappings[key]?.dotdigital_list_id || "");
      const originalList = String(originalLocationMappings[key]?.dotdigital_list_id || "");
      return Boolean(currentConfig && currentList) && (currentConfig !== originalConfig || currentList !== originalList);
    });
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  const hasIndependentLocationChanges = useMemo(() => {
    return (independentLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const currentConfig = String(locationMappings[key]?.dotdigital_config_id || "");
      const originalConfig = String(originalLocationMappings[key]?.dotdigital_config_id || "");
      const currentList = String(locationMappings[key]?.dotdigital_list_id || "");
      const originalList = String(originalLocationMappings[key]?.dotdigital_list_id || "");
      return Boolean(currentConfig && currentList) && (currentConfig !== originalConfig || currentList !== originalList);
    });
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const hasBrandHalfFilled = useMemo(() => checkHalfFilled(allBrandOwnedLocations), [allBrandOwnedLocations, locationMappings]);
  const hasIndependentHalfFilled = useMemo(() => checkHalfFilled(independentLocations), [independentLocations, locationMappings]);
  const selectedFieldMappings = useMemo(
    () => normalizeDotdigitalFieldMappings(fieldMappingsByConfigId[selectedFieldMappingConfigId]),
    [fieldMappingsByConfigId, selectedFieldMappingConfigId]
  );
  const hasFieldMappingChanges = useMemo(
    () => JSON.stringify(fieldMappingsByConfigId) !== JSON.stringify(originalFieldMappingsByConfigId),
    [fieldMappingsByConfigId, originalFieldMappingsByConfigId]
  );

  const handleFieldMappingChange = (sourceField, value) => {
    setFieldMappingsByConfigId((previousMappings) => ({
      ...previousMappings,
      [selectedFieldMappingConfigId]: {
        ...normalizeDotdigitalFieldMappings(previousMappings[selectedFieldMappingConfigId]),
        [sourceField]: value,
      },
    }));
  };

  const buildFieldMappingsPayload = () => Object.fromEntries(
    dotdigitalConfigs
      .filter((config) => config.persistedId)
      .map((config) => [
        config.persistedId,
        normalizeDotdigitalFieldMappings(fieldMappingsByConfigId[config.id]),
      ])
  );

  const handleSaveLocationMappings = async () => {
    try {
      setIsLoading(true);

      const selectedLocations = [
        ...(independentLocations || []),
        ...(allBrandOwnedLocations || []),
      ];

      const locationPayload = selectedLocations
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const currentConfigId = locationMappings[key]?.dotdigital_config_id || null;
          const currentList = locationMappings[key]?.dotdigital_list_id || null;
          const originalConfigId = originalLocationMappings[key]?.dotdigital_config_id || null;
          const originalList = originalLocationMappings[key]?.dotdigital_list_id || null;

          if (!currentConfigId || !currentList) {
            return null;
          }

          if (String(currentConfigId) === String(originalConfigId) && String(currentList) === String(originalList)) {
            return null;
          }

          const dotdigitalConfig = dotdigitalConfigs.find((config) => String(config.id) === String(currentConfigId));
          const dotdigitalList = getDotdigitalConfigLists(dotdigitalConfigs, currentConfigId)
            .find((list) => String(list.id) === String(currentList));

          if (!dotdigitalConfig?.persistedId) {
            return null;
          }

          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            dotdigital_config_id: dotdigitalConfig.persistedId,
            dotdigital_list_id: currentList,
            dotdigital_list_name: dotdigitalList?.name || "",
          };
        })
        .filter(Boolean);

      if (locationPayload.length === 0 && !hasFieldMappingChanges) {
        setToastMessage("No location changes to save");
        setIsLoading(false);
        return;
      }

      await saveDotdigitalMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
        field_mappings: buildFieldMappingsPayload(),
      });

      queryClient.invalidateQueries(["dotdigital-status"]);
      queryClient.invalidateQueries(["dotdigital-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["dotdigital-lists", userProfile.organisation_id]);

      const nextOriginalMappings = { ...originalLocationMappings };
      locationPayload.forEach((mapping) => {
        nextOriginalMappings[String(mapping.bconnect_location_id)] = {
          dotdigital_config_id: mapping.dotdigital_config_id || "",
          dotdigital_list_id: mapping.dotdigital_list_id,
        };
      });
      setOriginalLocationMappings(nextOriginalMappings);
      setOriginalFieldMappingsByConfigId(fieldMappingsByConfigId);

      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your selected Dotdigital lists.",
          integrationType: "dotdigital",
        },
      });
      setToastMessage("Location mappings saved successfully");
    } catch (error) {
      console.error("Error saving Dotdigital location mappings:", error);
      setToastMessage(error?.message || "Failed to save location mappings");
    } finally {
      setIsLoading(false);
    }
  };

  if (dotdigitalConfigs.length === 0) {
    return <div>No Dotdigital account data found. Please go back and validate your credentials again.</div>;
  }

  if (isLoadingBrands) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Dotdigital setup...</Text>
      </Flex>
    );
  }

  return (
    <Flex style={{ minHeight: "100vh" }} justify="center" align="center" p="6">
      <Card style={{ maxWidth: "1000px", width: "100%" }} p="4">
        <Flex style={{ alignSelf: "stretch" }} direction="column" align="start">
          <Flex style={{ alignSelf: "stretch" }} justify="between" align="center">
            <Flex align="center" gap="4">
              <Flex
                style={{
                  borderRadius: "var(--space-1)",
                  background: "#FFFFFF",
                  border: "1px solid var(--gray-a6)",
                }}
                height="40px"
                width="40px"
                p="2"
                justify="center"
                align="center"
              >
                <img src={require("../../../assets/images/dotdigtal.png")} alt="dotdigital" style={{ width: "24px" }} />
              </Flex>
              <Flex direction="column" justify="center" align="start">
                <Text size="4" weight="medium">Dotdigital</Text>
                <Text style={{ color: "var(--gray-a11)" }} size="1">
                  Map each b connect location to a Dotdigital account and list.
                </Text>
              </Flex>
            </Flex>

            <Flex align="center" gap="4">
              <Button size="2" variant="soft" onClick={() => navigate(-1)} color="gray">Cancel</Button>
              <Button
                size="2"
                variant="soft"
                onClick={handleSaveLocationMappings}
                loading={isLoading}
                disabled={
                  isLoading
                  || hasBrandHalfFilled
                  || hasIndependentHalfFilled
                  || !(hasBrandSectionChanges || hasIndependentLocationChanges || hasFieldMappingChanges)
                }
              >
                Save and finish
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="4" />

          <Flex style={{ alignSelf: "stretch" }} align="start" gap="5" mb="4">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Data fields</Text>
              <Text size="1" weight="regular">Map b connect fields to the matching Dotdigital data field names.</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              {dotdigitalConfigs.length > 1 ? (
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Dotdigital account</Text>
                  <Select.Root value={selectedFieldMappingConfigId || ""} onValueChange={setSelectedFieldMappingConfigId}>
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content position="popper">
                      {dotdigitalConfigs.map((config) => (
                        <Select.Item key={config.id} value={config.id}>
                          {getDotdigitalConfigLabel(config)}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>
              ) : null}

              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "46%" }}>b connect</Text>
                <Text size="2" weight="medium" style={{ width: "46%" }}>Dotdigital data field</Text>
              </Flex>

              {DOTDIGITAL_CUSTOM_FIELD_MAPPINGS.map((field) => (
                <Flex key={field.source} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                  <Flex style={{ width: "46%", minWidth: 0 }}>
                    <TextField.Root value={field.label} disabled style={{ width: "100%", backgroundColor: "var(--gray-3)" }} />
                  </Flex>
                  <Flex align="center" px="2">
                    <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                      <ArrowRightIcon width="16" height="16" />
                    </IconButton>
                  </Flex>
                  <Flex style={{ width: "46%", minWidth: 0 }}>
                    <TextField.Root
                      value={selectedFieldMappings[field.source] || ""}
                      onChange={(event) => handleFieldMappingChange(field.source, event.target.value)}
                      placeholder={field.defaultDotdigitalField}
                      style={{ width: "100%" }}
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="2" />

          <Flex style={{ alignSelf: "stretch" }} align="start" gap="5" mb="4">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Brands</Text>
              <Text size="1" weight="regular">Group: b connect brands with each of their locations. Each row now selects a Dotdigital account first, then a list from that account.</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital account</Text>
                <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital list</Text>
              </Flex>

              {(bconnectBrands || []).length > 0 ? (
                (bconnectBrands || []).map((brand) => {
                  const brandLocations = brandLocationsMap[brand.id] || [];
                  const brandSelection = brandSelections[String(brand.id)] || { configId: "", listId: "" };
                  const brandLists = getDotdigitalConfigLists(dotdigitalConfigs, brandSelection.configId);

                  return (
                    <Flex key={brand.id} direction="column" gap="2" style={{ width: "100%" }}>
                      <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                        <Text
                          size="2"
                          weight="medium"
                          style={{
                            flex: "1 1 0%",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          [{brand.name}]
                        </Text>

                        {brandLocations.length > 0 ? (
                          <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                            <Select.Root
                              value={brandSelection.configId || ""}
                              onValueChange={(value) => handleBrandConfigSelectionChange(brand.id, value)}
                            >
                              <Select.Trigger placeholder="Apply account..." style={{ width: "220px", boxSizing: "border-box" }}>
                                <div
                                  style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}
                                  title={brandSelection.configId ? (getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(brandSelection.configId))) || "") : "Apply account..."}
                                >
                                  {brandSelection.configId ? (getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(brandSelection.configId))) || "") : "Apply account..."}
                                </div>
                              </Select.Trigger>
                              <Select.Content side="bottom" align="start" sideOffset={4}>
                                {dotdigitalConfigs.map((config) => (
                                  <Select.Item key={config.id} value={config.id} title={getDotdigitalConfigLabel(config)}>
                                    {getDotdigitalConfigLabel(config)}
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Root>

                            <Select.Root
                              value={brandSelection.listId || ""}
                              onValueChange={(value) => handleBrandListSelectionChange(brand.id, value)}
                              disabled={!brandSelection.configId}
                            >
                              <Select.Trigger placeholder="Apply list..." style={{ width: "220px", boxSizing: "border-box" }}>
                                <div
                                  style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}
                                  title={brandSelection.listId ? (brandLists.find((list) => String(list.id) === String(brandSelection.listId))?.name || "") : (brandSelection.configId ? "Apply list..." : "Choose an account first")}
                                >
                                  {brandSelection.listId
                                    ? (brandLists.find((list) => String(list.id) === String(brandSelection.listId))?.name || "")
                                    : (brandSelection.configId ? "Apply list..." : "Choose an account first")}
                                </div>
                              </Select.Trigger>
                              <Select.Content side="bottom" align="start" sideOffset={4}>
                                {brandLists.map((list) => (
                                  <Select.Item key={list.id} value={list.id} title={list.name}>
                                    {list.name}
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Root>

                            <Button
                              size="1"
                              variant="soft"
                              onClick={() => handleApplyBrandList(brand.id)}
                              disabled={!brandSelection.configId || !brandSelection.listId}
                              style={{ flexShrink: 0 }}
                            >
                              Apply
                            </Button>
                          </Flex>
                        ) : null}
                      </Flex>

                      {brandLocations.length > 0 ? (
                        brandLocations.map((loc) => {
                          const locId = loc.id ?? loc.value ?? loc.location_id;
                          const key = String(locId);
                          const mapping = locationMappings[key] || {};
                          const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, mapping.dotdigital_config_id);
                          const selectedConfigLabel = mapping.dotdigital_config_id
                            ? getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(mapping.dotdigital_config_id)))
                            : "Select a Dotdigital account";
                          const selectedListName = mapping.dotdigital_list_id
                            ? availableLists.find((list) => String(list.id) === String(mapping.dotdigital_list_id))?.name || ""
                            : (mapping.dotdigital_config_id ? "Select a Dotdigital list" : "Choose an account first");

                          return (
                            <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                              <Flex style={{ width: "32%", minWidth: 0 }}>
                                <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                  <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                    {loc.name ?? loc.label ?? ""}
                                  </Text>
                                </Flex>
                              </Flex>

                              <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                  <ArrowRightIcon width="16" height="16" />
                                </IconButton>
                              </Flex>

                              <Flex style={{ width: "32%", minWidth: 0 }}>
                                <Select.Root value={mapping.dotdigital_config_id || ""} onValueChange={(value) => handleLocationConfigChange(key, value)}>
                                  <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedConfigLabel}>
                                      {selectedConfigLabel}
                                    </div>
                                  </Select.Trigger>
                                  <Select.Content side="bottom" align="start" sideOffset={4}>
                                    {dotdigitalConfigs.map((config) => (
                                      <Select.Item key={config.id} value={config.id} title={getDotdigitalConfigLabel(config)}>
                                        {getDotdigitalConfigLabel(config)}
                                      </Select.Item>
                                    ))}
                                  </Select.Content>
                                </Select.Root>
                              </Flex>

                              <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                  <ArrowRightIcon width="16" height="16" />
                                </IconButton>
                              </Flex>

                              <Flex style={{ width: "32%", minWidth: 0 }}>
                                <Select.Root
                                  value={mapping.dotdigital_list_id || ""}
                                  onValueChange={(value) => handleLocationDotdigitalListChange(key, value)}
                                  disabled={!mapping.dotdigital_config_id}
                                >
                                  <Select.Trigger placeholder="Select list..." style={{ width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedListName}>
                                      {selectedListName}
                                    </div>
                                  </Select.Trigger>
                                  <Select.Content side="bottom" align="start" sideOffset={4}>
                                    {availableLists.map((list) => (
                                      <Select.Item key={list.id} value={list.id} title={list.name}>
                                        {list.name}
                                      </Select.Item>
                                    ))}
                                  </Select.Content>
                                </Select.Root>
                              </Flex>
                            </Flex>
                          );
                        })
                      ) : (
                        <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                        </Flex>
                      )}
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No brands found.</Text>
                </Flex>
              )}
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="2" />

          <Flex style={{ alignSelf: "stretch" }} align="start" gap="5">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Locations</Text>
              <Text size="1" weight="regular">Map independent b connect locations to a Dotdigital account and list.</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital account</Text>
                <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital list</Text>
              </Flex>

              {(independentLocations || []).length > 0 ? (
                independentLocations.map((loc) => {
                  const locId = loc.id ?? loc.value ?? loc.location_id;
                  const key = String(locId);
                  const mapping = locationMappings[key] || {};
                  const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, mapping.dotdigital_config_id);
                  const selectedConfigLabel = mapping.dotdigital_config_id
                    ? getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(mapping.dotdigital_config_id)))
                    : "Select a Dotdigital account";
                  const selectedListName = mapping.dotdigital_list_id
                    ? availableLists.find((list) => String(list.id) === String(mapping.dotdigital_list_id))?.name || ""
                    : (mapping.dotdigital_config_id ? "Select a Dotdigital list" : "Choose an account first");

                  return (
                    <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                      <Flex style={{ width: "32%", minWidth: 0 }}>
                        <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                          <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                            {loc.name ?? loc.label ?? ""}
                          </Text>
                        </Flex>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "32%", minWidth: 0 }}>
                        <Select.Root value={mapping.dotdigital_config_id || ""} onValueChange={(value) => handleLocationConfigChange(key, value)}>
                          <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                            <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedConfigLabel}>
                              {selectedConfigLabel}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            {dotdigitalConfigs.map((config) => (
                              <Select.Item key={config.id} value={config.id} title={getDotdigitalConfigLabel(config)}>
                                {getDotdigitalConfigLabel(config)}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "32%", minWidth: 0 }}>
                        <Select.Root
                          value={mapping.dotdigital_list_id || ""}
                          onValueChange={(value) => handleLocationDotdigitalListChange(key, value)}
                          disabled={!mapping.dotdigital_config_id}
                        >
                          <Select.Trigger placeholder="Select list..." style={{ width: "100%", boxSizing: "border-box" }}>
                            <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedListName}>
                              {selectedListName}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            {availableLists.map((list) => (
                              <Select.Item key={list.id} value={list.id} title={list.name}>
                                {list.name}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </Flex>
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No independent locations found.</Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
}
